<template>
  <touch-layout>
    <template #header_page_breadcrumb>
      <slot name="header_page_breadcrumb" />
    </template>
    <template #header_page_title>
      <slot name="header_page_title" />
    </template>
    <slot name="menu" />
    <div class="flex flex-col grow">
      <div class="flex flex-col md:flex-row gap-5 p-5 grow bg-gray-200 w-full overflow-y-auto md:overflow-y-auto" style="flex: 1 1 0">
        <div class="flex flex-col gap-2 bg-white p-5 border border-gray shrink-0" style="min-width: 250px">
          <loading v-if="isLoadingProducts" :loading="true" class="h-full" style="min-height: 200px; flex: 1 1 0" />
          <div
            v-else-if="products.length > 0"
            class="overflow-y-auto" 
            style="min-height: 200px; min-width: 200px; flex: 1 1 0"
          >
            <div
              v-for="product in products"
              :key="product.id"
              class="mb-2 p-2 rounded bg-gray-200 cursor-pointer"
              :class="{ 'bg-green-600 text-white': product.engineId == $route.query.productId }"
              @click="$router.push({ query: { ...$route.query, productId: product.engineId } })"
            >
              {{ product.description }}
            </div>
          </div>
          <div v-else class="overflow-hidden flex flex-col grow justify-around text-center px-5"> No Products Found</div>
        </div>
        <div class="flex flex-col gap-2 bg-white p-5 w-full border border-gray">
          <loading v-if="isLoadingSashOverlaps" :loading="true" class="h-full" style="min-height: 200px; flex: 1 1 0" />
          <div v-else-if="sashOverlaps.length > 0" class="md:overflow-y-auto" style="flex: 1 1 0">
            <div class="grow bg-white">
              <table class="table-unstacked _with-hover w-full table-fixed">
                <thead class="top-0 bg-white z-10 text-left hidden sm:table-header-group sticky">
                  <tr class="pt-5">
                    <th class="pb-4">Description</th>
                    <th class="pb-4">Sash Overlap</th>
                    <th class="pb-4">Active</th>
                  </tr>
                </thead>
                <tbody v-for="sashOverlapHeading in sashOverlapHeadings" :key="sashOverlapHeading.id">
                  <tr>
                    <td colspan="3" class="cols-span-3 !px-0">
                      <div class="my-2 w-full">
                        <div class="bg-gray-200 text-center rounded w-full p-3">
                          <b>{{ sashOverlapHeading.optionHeadingDescription }}</b>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr v-for="sashOverlap in sashOverlapHeading.items" :key="sashOverlap.id">
                    <td class="px-4 py-2">
                      {{ sashOverlap.optionItemDescription }}
                    </td>
                    <td class="px-4 py-2">
                      <div class="flex items-center bg-white rounded border overflow-hidden w-40">
                        <input
                          v-model.number="sashOverlap.sizeOverlap"
                          type="number"
                          class="grow p-2 w-24"
                          min="0"
                        >
                        <div class="ml-auto border-l px-2">
                          <span>mm</span>
                        </div>
                      </div>
                    </td>
                    <td class="px-4 py-2">
                      <div class="ml-auto flex items-center gap-5">
                        <span class="text-xs opacity-50">{{
                          sashOverlap.isActive ? 'Enabled' : 'Disabled'
                        }}</span>
                        <div class="relative cursor-pointer" @click.stop="sashOverlap.isActive = !sashOverlap.isActive">
                          <div class="w-6 h-3 bg-gray-400 rounded-full shadow-inner" />
                          <div class="switch-toggle" :class="{ _active: sashOverlap.isActive }" />
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-else class="overflow-hidden flex flex-col grow justify-around text-center px-5"> No Sash Overlaps Found</div>
        </div>
      </div>
      <div class="sticky bottom-0 bg-white text-right p-3 border-t">
        <button
          class="btn btn-action btn-lg"
          :class="{ 'opacity-50 pointer-events-none bg-gray-500 border-gray-500': !isChanged }"
          @click="save()"
        >
          Save Changes
        </button>
      </div>
    </div>
  </touch-layout>
</template>

<script>

import { mapStores } from 'pinia'
import { useCustomerStore } from '@/pinia/customer';

export default {
  beforeRouteUpdate(to, from, next) {
    this.promptToSave(() => next())
  },
  beforeRouteLeave(to, from, next) {
    if (to.name === 'Error') {
      next()
    } else {
      this.promptToSave(() => next())
    }
  },
  data() {
    return {
      sashOverlaps: [],
      originalSashOverlaps: [],
      products: [],
      loading: true,
      isLoadingProducts: false,
      isLoadingSashOverlaps: false
    };
  },
  computed: {
    ...mapStores(useCustomerStore),
    isChanged() {
      return JSON.stringify(this.sashOverlaps) !== JSON.stringify(this.originalSashOverlaps)
    },
    sashOverlapHeadings() {
      return this.sashOverlaps.reduce((acc, item) => {
        if (!acc.filter(x => x.optionHeadingTypeId === item.optionHeadingTypeId).length) {
          acc.push({
            optionHeadingTypeId: item.optionHeadingTypeId,
            optionHeadingDescription: item.optionHeadingDescription,
            items: []
          })
        }
        acc.find(x => x.optionHeadingTypeId === item.optionHeadingTypeId).items.push(item)
        return acc;
      }, []);
    }

  },
  watch: {
    async '$route.query'({ productId }) {
      if (productId) {
        await this.GetCatalogueOptionsForOverlaps()
      }
    },
  },
  async created() {
    await this.getProducts()
    if (this.$route.query.productId) {
      await this.GetCatalogueOptionsForOverlaps()
    } else {
      this.$router.replace({ query: { ...this.$route.query, productId: this.products?.[0]?.engineId } })
    }
  },
  methods: {
    async getProducts() {
      this.isLoadingProducts = true
      const { products } = await window.touch.GetCatalogueProductsForOverlaps({
        datasetId: this.customerStore.customer.datasetIds[0]
      })
      this.products = products
      this.isLoadingProducts = false
    },
    async GetCatalogueOptionsForOverlaps() {
      this.isLoadingSashOverlaps = true
      const { sashOverlaps } = await window.touch.GetCatalogueOptionsForOverlaps({
        datasetId: this.customerStore.customer.datasetIds[0],
        productId: this.$route.query.productId
      })
      this.sashOverlaps = sashOverlaps
      this.originalSashOverlaps = JSON.parse(JSON.stringify(this.sashOverlaps))
      this.isLoadingSashOverlaps = false
    },
    promptToSave(callback) {
      if (this.isChanged) {
        this.alertBox()
          .fire({
            title: 'There are unsaved changes',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: 'Go Back',
            confirmButtonText: 'Continue',
          })
          .then((result) => {
            if (result.value) {
              callback()
            }
          })
      } else {
        callback()
      }
    },
    async save() {
      this.isLoadingFinancials = true
      const changedItems = this.getChanged()
      try {
        await window.touch.UpdateCatalogueSashOverlaps(
          { ...changedItems, DatasetId: this.customerStore.customer.datasetIds[0] }
        )
      } catch (error) {
        if (error.status === 400) {
          this.alertBox().fire(error.popUp);
        } else {
          throw error
        }
      }
      await this.GetCatalogueOptionsForOverlaps()
      this.isLoadingFinancials = false
    },
    getChanged() {
      let changedItems = [];
      if (this.isChanged) {
        for (let index = 0; index < this.sashOverlaps.length; index += 1) {
          const sashOverlaps = this.sashOverlaps[index]
          const originalSashOverlaps = this.originalSashOverlaps[index]
          if (JSON.stringify(sashOverlaps) !== JSON.stringify(originalSashOverlaps)) {
            changedItems.push({
              ...sashOverlaps,
            });
          }
        }
      }
      return {
        SashOverlaps: changedItems
      }
    },
  }
};
</script>

<style scoped>
@media (min-width: 768px) {
  .xs\:table-unstacked td {
    padding-top: 0.3rem;
    padding-bottom: 0.3rem;
  }

  td {
    padding-left: 0;
  }
}
</style>